import * as React from "react";
import { Card, CardProps, Box } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

export interface GlassCardProps extends CardProps {
  children: React.ReactNode;
  effect?: "clear" | "regular" | "none";
  interactive?: boolean;
  colorScheme?: "light" | "dark" | "system";
  hoverable?: boolean; // Keep for backward compatibility
  gradient?: boolean; // Keep for backward compatibility
  glassIntensity?: "light" | "regular" | "strong"; // Keep for backward compatibility
  nested?: boolean; // For nested cards that shouldn't blur parent
}

/**
 * GlassCard component that provides glassmorphism effect similar to iOS Liquid Glass
 * If effect is 'none', returns simple Card without glass effect to preserve backgroundColor
 */
const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(({
  children,
  effect,
  interactive = true,
  colorScheme = "system",
  hoverable = false,
  gradient = false,
  glassIntensity, // For backward compatibility
  nested = false,
  sx,
  className,
  ...props
}, ref) => {
  const theme = useTheme();
  const systemIsDark = theme.palette.mode === "dark";


  // Determine color scheme
  const isDark = colorScheme === "system"
    ? systemIsDark
    : colorScheme === "dark";

  // Map old glassIntensity to new effect prop for backward compatibility
  const resolvedEffect = effect || (glassIntensity === "light" ? "clear" : glassIntensity === "strong" ? "regular" : "regular");

  // If effect is 'none', return simple Card without glass effect
  if (resolvedEffect === "none") {
    return (
      <Card
        className={className}
        sx={{
          overflow: "hidden",
          borderRadius: (theme) => theme.shape.borderRadius * 1.25, // Less rounded
          [theme.breakpoints.down("sm")]: {
            borderRadius: (theme) => theme.shape.borderRadius * 1,
          },
          "@media (orientation: landscape) and (max-width: 900px)": {
            borderRadius: (theme) => theme.shape.borderRadius * 0.75,
          },
          ...sx,
        }}
        {...props}
      >
        {children}
      </Card>
    );
  }

  // Native liquid glass effect configuration - enhanced for stronger white blur effect
  const glassConfig = {
    clear: {
      blur: "blur(30px) saturate(200%)",
      bgLight: alpha("#ffffff", 0.5),
      bgDark: alpha("#ffffff", 0.3),
      borderLight: alpha("#ffffff", 0.6),
      borderDark: alpha("#ffffff", 0.5),
      shadowLight: "0 8px 32px 0 rgba(31, 38, 135, 0.2)",
      shadowDark: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
      insetLight: "inset 0 1px 0 0 rgba(255, 255, 255, 0.5)",
      insetDark: "inset 0 1px 0 0 rgba(255, 255, 255, 0.3)",
    },
    regular: {
      blur: "blur(35px) saturate(220%)",
      bgLight: alpha("#ffffff", 0.6),
      bgDark: alpha("#ffffff", 0.4),
      borderLight: alpha("#ffffff", 0.7),
      borderDark: alpha("#ffffff", 0.6),
      shadowLight: "0 8px 32px 0 rgba(31, 38, 135, 0.25)",
      shadowDark: "0 8px 32px 0 rgba(0, 0, 0, 0.6)",
      insetLight: "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
      insetDark: "inset 0 1px 0 0 rgba(255, 255, 255, 0.4)",
    },
  };

  const config = glassConfig[resolvedEffect];
  const shouldHover = hoverable || interactive;

  // For nested cards, use glass effect without backdrop-filter to avoid blurring parent
  if (nested) {
    return (
      <Box
        className={`glass-effect ${isDark ? "glass-effect-dark" : ""} glass-transition ${shouldHover ? "glass-hover" : ""} ${className || ""}`}
        sx={{
          position: "relative",
          borderRadius: (theme) => theme.shape.borderRadius * 1.25,
          overflow: "hidden",
          border: `1px solid ${isDark ? config.borderDark : config.borderLight}`,
          // For nested, use more transparent background without backdrop-filter for better liquid glass effect
          background: isDark
            ? `linear-gradient(135deg, ${alpha("#ffffff", 0.08)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
            : `linear-gradient(135deg, ${alpha("#ffffff", 0.15)} 0%, ${alpha("#ffffff", 0.1)} 100%)`,
          boxShadow: isDark ? config.shadowDark : config.shadowLight,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          [theme.breakpoints.down("sm")]: {
            borderRadius: (theme) => theme.shape.borderRadius * 1,
          },
          "@media (orientation: landscape) and (max-width: 900px)": {
            borderRadius: (theme) => theme.shape.borderRadius * 0.75,
          },
          // Inset highlight for glass effect
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: (theme) => theme.shape.borderRadius * 1.25,
            pointerEvents: "none",
            boxShadow: isDark ? config.insetDark : config.insetLight,
            zIndex: 1,
            [theme.breakpoints.down("sm")]: {
              borderRadius: (theme) => theme.shape.borderRadius * 1,
            },
            "@media (orientation: landscape) and (max-width: 900px)": {
              borderRadius: (theme) => theme.shape.borderRadius * 0.75,
            },
          },
          ...(shouldHover && {
            cursor: "pointer",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: isDark
                ? "0 12px 48px 0 rgba(0, 0, 0, 0.6)"
                : "0 12px 48px 0 rgba(0, 0, 0, 0.2)",
              border: `1px solid ${isDark ? alpha("#ffffff", 0.7) : alpha("#ffffff", 0.8)}`,
            },
          }),
          ...sx,
        }}
        {...props}
      >
        {/* Gradient overlays for glass shine effect - more transparent for nested */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "50%",
            background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.1 : 0.15)} 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, transparent 0%, ${alpha("#ffffff", 0.2)} 50%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            width: "4px",
            background: `linear-gradient(180deg, transparent 0%, ${alpha("#ffffff", 0.2)} 50%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        {/* Additional white overlay for nested cards */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha("#ffffff", isDark ? 0.25 : 0.35)} 0%, transparent 50%, ${alpha("#ffffff", isDark ? 0.2 : 0.3)} 100%)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        {/* Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            width: "100%",
            height: "100%",
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      className={`glass-effect ${isDark ? "glass-effect-dark" : ""} glass-transition ${shouldHover ? "glass-hover" : ""} ${className || ""}`}
      sx={{
        position: "relative",
        borderRadius: (theme) => theme.shape.borderRadius * 1.25, // Less rounded
        overflow: "hidden",
        border: `1px solid ${isDark ? config.borderDark : config.borderLight}`,
        background: isDark ? config.bgDark : config.bgLight,
        backdropFilter: config.blur,
        WebkitBackdropFilter: config.blur,
        boxShadow: isDark ? config.shadowDark : config.shadowLight,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        [theme.breakpoints.down("sm")]: {
          borderRadius: (theme) => theme.shape.borderRadius * 1,
        },
        "@media (orientation: landscape) and (max-width: 900px)": {
          borderRadius: (theme) => theme.shape.borderRadius * 0.75,
        },
        // Inset highlight for glass effect
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: (theme) => theme.shape.borderRadius * 1.25,
          pointerEvents: "none",
          boxShadow: isDark ? config.insetDark : config.insetLight,
          zIndex: 1,
          [theme.breakpoints.down("sm")]: {
            borderRadius: (theme) => theme.shape.borderRadius * 1,
          },
          "@media (orientation: landscape) and (max-width: 900px)": {
            borderRadius: (theme) => theme.shape.borderRadius * 0.75,
          },
        },
        ...(shouldHover && {
          cursor: "pointer",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: isDark
              ? "0 12px 48px 0 rgba(0, 0, 0, 0.6)"
              : "0 12px 48px 0 rgba(0, 0, 0, 0.2)",
            border: `1px solid ${isDark ? alpha("#ffffff", 0.7) : alpha("#ffffff", 0.8)}`,
          },
        }),
        ...sx,
      }}
      {...props}
    >
      {/* Gradient overlays for glass shine effect - enhanced for stronger white effect */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "50%",
          background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.25 : 0.4)} 0%, transparent 100%)`,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: `linear-gradient(90deg, transparent 0%, ${alpha("#ffffff", 0.5)} 50%, transparent 100%)`,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: "4px",
          background: `linear-gradient(180deg, transparent 0%, ${alpha("#ffffff", 0.5)} 50%, transparent 100%)`,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      {/* Additional white overlay for non-nested cards */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha("#ffffff", isDark ? 0.1 : 0.15)} 0%, transparent 50%, ${alpha("#ffffff", isDark ? 0.05 : 0.1)} 100%)`,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          height: "100%",
        }}
      >
        {children}
      </Box>
    </Box>
  );
});

GlassCard.displayName = "GlassCard";

export default GlassCard;